import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { useTheme, adminTheme } from '@/context/ThemeContext';
import { usePushNotification } from '@/context/PushNotificationContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  MessageCircle,
  Send,
  User,
  Clock,
  CheckCircle,
  XCircle,
  Search,
  Phone,
  Mail,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

export default function LiveChat() {
  const { isDark } = useTheme();
  const { notifyNewChat } = usePushNotification();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const messagesEndRef = useRef(null);
  const audioRef = useRef(null);
  const previousConvIdsRef = useRef(new Set());
  const isFirstLoadRef = useRef(true);

  // Get admin name from localStorage (set during login)
  const getAdminName = () => {
    try {
      const userStr = localStorage.getItem('userData');
      if (userStr) {
        const user = JSON.parse(userStr);
        return user.name || user.email?.split('@')[0] || 'Admin';
      }
    } catch {
      return 'Admin';
    }
    return 'Admin';
  };

  // Fetch conversations
  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/conversations`);
      const newConversations = response.data;
      
      // Get current conversation IDs
      const currentIds = new Set(newConversations.map(c => c.id));
      
      // Check for new conversations or new messages
      if (!isFirstLoadRef.current) {
        // Find new conversations
        newConversations.forEach(conv => {
          if (!previousConvIdsRef.current.has(conv.id)) {
            // New conversation started
            notifyNewChat(conv);
            playNotificationSound();
          }
        });
        
        // Check for new messages in existing conversations
        const totalUnread = newConversations.reduce((sum, c) => sum + (c.unread_count || 0), 0);
        const prevTotalUnread = conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0);
        
        if (totalUnread > prevTotalUnread) {
          // Find which conversation has new messages
          newConversations.forEach(conv => {
            const prevConv = conversations.find(c => c.id === conv.id);
            if (prevConv && (conv.unread_count || 0) > (prevConv.unread_count || 0)) {
              notifyNewChat(conv);
              playNotificationSound();
            }
          });
        }
      }
      
      // Update previous IDs reference
      previousConvIdsRef.current = currentIds;
      isFirstLoadRef.current = false;
      
      setConversations(newConversations);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch messages for selected conversation
  const fetchMessages = async () => {
    if (!selectedConversation) return;
    try {
      const response = await axios.get(`${API}/conversations/${selectedConversation.id}/messages`);
      setMessages(response.data);
      
      // Mark conversation as read
      if (selectedConversation.unread_count > 0) {
        await axios.put(`${API}/conversations/${selectedConversation.id}/read`);
        fetchConversations();
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  // Play notification sound
  const playNotificationSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (error) {
      console.log('Audio playback failed:', error);
    }
  };

  // Send message as admin
  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    const messageText = newMessage.trim();
    const adminName = getAdminName();
    setNewMessage('');
    setSending(true);

    // Optimistically add message
    const tempMessage = {
      id: Date.now().toString(),
      conversation_id: selectedConversation.id,
      sender: 'admin',
      admin_name: adminName,
      message: messageText,
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempMessage]);

    try {
      await axios.post(`${API}/conversations/${selectedConversation.id}/messages/admin`, {
        message: messageText,
        admin_name: adminName
      });
      fetchConversations();
    } catch (error) {
      toast.error('Failed to send message');
      setMessages(prev => prev.filter(m => m.id !== tempMessage.id));
    } finally {
      setSending(false);
    }
  };

  // Close conversation
  const closeConversation = async (convId) => {
    try {
      await axios.put(`${API}/conversations/${convId}/close`);
      toast.success('Conversation closed');
      fetchConversations();
      if (selectedConversation?.id === convId) {
        setSelectedConversation(null);
        setMessages([]);
      }
    } catch (error) {
      toast.error('Failed to close conversation');
    }
  };

  // Poll for updates
  useEffect(() => {
    fetchConversations();
    const interval = setInterval(fetchConversations, 5000);
    return () => clearInterval(interval);
  }, []);

  // Fetch messages when conversation is selected
  useEffect(() => {
    if (selectedConversation) {
      fetchMessages();
      const interval = setInterval(fetchMessages, 3000);
      return () => clearInterval(interval);
    }
  }, [selectedConversation?.id]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const filteredConversations = conversations.filter(conv =>
    conv.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    conv.customer_email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const activeConversations = filteredConversations.filter(c => c.status === 'active');
  const closedConversations = filteredConversations.filter(c => c.status === 'closed');
  const totalUnread = conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0);

  return (
    <div className="space-y-6" data-testid="live-chat">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className={`text-2xl font-bold ${adminTheme.textPrimary(isDark)}`}>Live Chat</h1>
          <p className={adminTheme.textSecondary(isDark)}>Manage customer conversations in real-time</p>
        </div>
        <div className="flex items-center gap-3">
          {totalUnread > 0 && (
            <Badge className="bg-red-500 text-white">
              {totalUnread} unread message{totalUnread > 1 ? 's' : ''}
            </Badge>
          )}
          <Button variant="outline" size="sm" onClick={fetchConversations} className={isDark ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : ''}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[calc(100vh-220px)]">
        {/* Conversations List */}
        <Card className={`lg:col-span-1 flex flex-col border ${adminTheme.cardBg(isDark)}`}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className={`text-lg ${adminTheme.textPrimary(isDark)}`}>Conversations</CardTitle>
              <Badge variant="outline" className={isDark ? 'border-slate-600 text-slate-300' : ''}>{activeConversations.length} active</Badge>
            </div>
            <div className="relative mt-2">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${adminTheme.textMuted(isDark)}`} />
              <Input
                placeholder="Search conversations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`pl-9 h-9 ${adminTheme.inputBg(isDark)}`}
              />
            </div>
          </CardHeader>
          <CardContent className="flex-1 overflow-hidden p-0">
            <ScrollArea className="h-full">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className={`w-6 h-6 animate-spin ${adminTheme.textMuted(isDark)}`} />
                </div>
              ) : filteredConversations.length === 0 ? (
                <div className="text-center py-8 px-4">
                  <MessageCircle className={`w-12 h-12 mx-auto mb-3 ${adminTheme.textMuted(isDark)}`} />
                  <p className={adminTheme.textSecondary(isDark)}>No conversations yet</p>
                  <p className={`text-xs mt-1 ${adminTheme.textMuted(isDark)}`}>Customer chats will appear here</p>
                </div>
              ) : (
                <div className={`divide-y ${adminTheme.border(isDark)}`}>
                  {/* Active Conversations */}
                  {activeConversations.map((conv) => (
                    <div
                      key={conv.id}
                      onClick={() => setSelectedConversation(conv)}
                      className={cn(
                        `p-4 cursor-pointer transition-colors ${adminTheme.cardHover(isDark)}`,
                        selectedConversation?.id === conv.id && (isDark ? 'bg-blue-900/30 border-l-4 border-blue-500' : 'bg-blue-50 border-l-4 border-blue-500')
                      )}
                    >
                      <div className="flex items-start gap-3">
                        <div className="relative">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isDark ? 'bg-blue-900/50' : 'bg-blue-100'}`}>
                            <User className="w-5 h-5 text-blue-500" />
                          </div>
                          <span className={`absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full ${isDark ? 'border-2 border-slate-900' : 'border-2 border-white'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className={`font-medium truncate ${adminTheme.textPrimary(isDark)}`}>{conv.customer_name}</p>
                            {conv.unread_count > 0 && (
                              <Badge className="bg-red-500 text-white text-xs px-1.5 min-w-[20px] h-5">
                                {conv.unread_count}
                              </Badge>
                            )}
                          </div>
                          <p className={`text-sm truncate ${adminTheme.textSecondary(isDark)}`}>{conv.last_message || 'No messages yet'}</p>
                          <p className={`text-xs mt-1 ${adminTheme.textMuted(isDark)}`}>{formatTime(conv.last_message_at || conv.created_at)}</p>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Closed Conversations */}
                  {closedConversations.length > 0 && (
                    <>
                      <div className={`px-4 py-2 ${isDark ? 'bg-slate-800' : 'bg-slate-100'}`}>
                        <p className={`text-xs font-medium uppercase ${adminTheme.textMuted(isDark)}`}>Closed</p>
                      </div>
                      {closedConversations.map((conv) => (
                        <div
                          key={conv.id}
                          onClick={() => setSelectedConversation(conv)}
                          className={cn(
                            `p-4 cursor-pointer transition-colors opacity-60 ${adminTheme.cardHover(isDark)}`,
                            selectedConversation?.id === conv.id && (isDark ? 'bg-slate-800' : 'bg-slate-100')
                          )}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isDark ? 'bg-slate-700' : 'bg-slate-200'}`}>
                              <User className={`w-5 h-5 ${adminTheme.textMuted(isDark)}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`font-medium truncate ${adminTheme.textSecondary(isDark)}`}>{conv.customer_name}</p>
                              <p className={`text-sm truncate ${adminTheme.textMuted(isDark)}`}>{conv.last_message || 'No messages'}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Chat Area */}
        <Card className={`lg:col-span-2 flex flex-col border ${adminTheme.cardBg(isDark)}`}>
          {selectedConversation ? (
            <>
              {/* Chat Header */}
              <CardHeader className={`pb-4 ${adminTheme.border(isDark)} border-b`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isDark ? 'bg-blue-900/50' : 'bg-blue-100'}`}>
                        <User className="w-5 h-5 text-blue-500" />
                      </div>
                      {selectedConversation.status === 'active' && (
                        <span className={`absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full ${isDark ? 'border-2 border-slate-900' : 'border-2 border-white'}`} />
                      )}
                    </div>
                    <div>
                      <p className={`font-medium ${adminTheme.textPrimary(isDark)}`}>{selectedConversation.customer_name}</p>
                      <div className={`flex items-center gap-3 text-xs ${adminTheme.textSecondary(isDark)}`}>
                        {selectedConversation.customer_email && (
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {selectedConversation.customer_email}
                          </span>
                        )}
                        {selectedConversation.customer_phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {selectedConversation.customer_phone}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedConversation.status === 'active' ? (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => closeConversation(selectedConversation.id)}
                        className={isDark ? 'text-red-400 border-red-800 hover:text-red-300 hover:bg-red-900/30' : 'text-red-600 hover:text-red-700 hover:bg-red-50'}
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Close Chat
                      </Button>
                    ) : (
                      <Badge variant="outline" className={isDark ? 'text-slate-400 border-slate-600' : 'text-slate-500'}>
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Closed
                      </Badge>
                    )}
                  </div>
                </div>
              </CardHeader>

              {/* Messages */}
              <CardContent className="flex-1 overflow-hidden p-0">
                <ScrollArea className="h-full p-4">
                  <div className="space-y-4">
                    {messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={cn(
                          'flex gap-2',
                          msg.sender === 'admin' ? 'flex-row-reverse' : ''
                        )}
                      >
                        {msg.sender === 'customer' && (
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-blue-900/50' : 'bg-blue-100'}`}>
                            <User className="w-4 h-4 text-blue-500" />
                          </div>
                        )}
                        <div
                          className={cn(
                            'rounded-2xl px-4 py-2 max-w-[70%]',
                            msg.sender === 'admin'
                              ? 'bg-blue-600 text-white rounded-tr-sm'
                              : (isDark ? 'bg-slate-800 text-slate-200 rounded-tl-sm' : 'bg-slate-100 text-slate-700 rounded-tl-sm')
                          )}
                        >
                          <p className="text-sm">{msg.message}</p>
                          <p className={cn(
                            'text-xs mt-1',
                            msg.sender === 'admin' ? 'text-blue-200' : (isDark ? 'text-slate-500' : 'text-slate-400')
                          )}>
                            {msg.admin_name && msg.sender === 'admin' && <span className="font-medium">{msg.admin_name} • </span>}
                            {formatTime(msg.created_at)}
                          </p>
                        </div>
                        {msg.sender === 'admin' && (
                          <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center flex-shrink-0" title={msg.admin_name || 'Admin'}>
                            <span className="text-white text-xs font-bold">{(msg.admin_name || 'A').charAt(0).toUpperCase()}</span>
                          </div>
                        )}
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>
              </CardContent>

              {/* Message Input */}
              {selectedConversation.status === 'active' && (
                <div className={`p-4 border-t ${adminTheme.border(isDark)}`}>
                  <form onSubmit={sendMessage} className="flex gap-2">
                    <Input
                      placeholder="Type your reply..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      className={`flex-1 ${adminTheme.inputBg(isDark)}`}
                      disabled={sending}
                    />
                    <Button type="submit" disabled={sending || !newMessage.trim()} className="bg-blue-600 hover:bg-blue-700">
                      {sending ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </Button>
                  </form>
                </div>
              )}
            </>
          ) : (
            // No conversation selected
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageCircle className={`w-16 h-16 mx-auto mb-4 ${adminTheme.textMuted(isDark)}`} />
                <h3 className={`text-lg font-medium mb-2 ${adminTheme.textSecondary(isDark)}`}>Select a conversation</h3>
                <p className={`text-sm ${adminTheme.textMuted(isDark)}`}>
                  Choose a conversation from the list to start chatting
                </p>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
